// src/lib/push.js — F-025-T1
//
// Client-side push registration + preferences helpers for the PWA web-push
// path. NO Capacitor import (native push is F-025-T2). The firebase SDK is
// pulled in via dynamic import so it only loads when the user actually
// interacts with the notifications control in Settings.
//
// Auth pattern mirrors ShuffleCard/StandaloneGamesPanel: read the Supabase
// session and send the access token as a Bearer header. src/lib/db.js is NOT
// used here — it does not attach auth, and these endpoints derive player_id
// from the verified JWT.

import { supabase } from './supabase'

// Capability guard — only offer push where the browser supports the whole
// chain. Call before showing the "Enable notifications" control.
export function pushSupported() {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  )
}

// Current browser permission state: 'default' | 'granted' | 'denied'.
export function notificationPermission() {
  if (typeof Notification === 'undefined') return 'denied'
  return Notification.permission
}

// Synchronous "is FCM configured on this build?" check. Mirrors
// isFirebaseConfigured() in ./firebase, but reads the Vite env directly so a
// caller (MePage) can gate on config WITHOUT statically importing ./firebase —
// a static import would pull the firebase SDK into the caller's chunk and
// defeat the lazy `await import('./firebase')` that keeps it code-split. Keep
// the field list in sync with firebase.js's isFirebaseConfigured().
export function isPushConfigured() {
  return Boolean(
    import.meta.env.VITE_FIREBASE_API_KEY &&
    import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID &&
    import.meta.env.VITE_FIREBASE_APP_ID &&
    import.meta.env.VITE_FIREBASE_VAPID_KEY
  )
}

// Should we show the in-app soft-ask pre-prompt? True only when push is
// configured on this build, the browser supports the whole chain, AND the
// user hasn't decided yet (permission === 'default'). Once 'granted' or
// 'denied' this returns false — browsers permanently block re-asking after a
// denial, so we never nag. Kept synchronous + pure so the banner gate is
// unit-testable and reused rather than duplicated.
export function shouldSoftAsk() {
  return isPushConfigured() && pushSupported() && notificationPermission() === 'default'
}

async function authedFetch(fn, action, payload = {}) {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) throw new Error('Please sign in again.')
  const res = await fetch(`/.netlify/functions/${fn}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ action, ...payload }),
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok) {
    const err = new Error(json.error || `${action} failed`)
    err.code = json.code
    throw err
  }
  return json.data
}

// Full enable flow, gated behind an explicit user tap (never auto-run on
// load). Requests OS permission, gets an FCM token, and registers it with the
// server. Returns { ok, reason }.
export async function enablePush() {
  if (!pushSupported()) return { ok: false, reason: 'unsupported' }

  const perm = await Notification.requestPermission()
  if (perm !== 'granted') return { ok: false, reason: 'permission_denied' }

  const { requestFcmToken } = await import('./firebase')
  const token = await requestFcmToken()
  if (!token) return { ok: false, reason: 'no_token' }

  await authedFetch('push-register', 'register', {
    token,
    platform: 'web',
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
  })
  return { ok: true, token }
}

// Foreground message subscription — resolves an unsubscribe function. The
// caller renders the toast; we don't roll a new toast system here.
export async function subscribeForegroundMessages(handler) {
  const { onForegroundMessage } = await import('./firebase')
  return onForegroundMessage(handler)
}

// Ask the server to push a test notification to the caller's own devices —
// the single end-to-end proof of the pipe.
export async function sendTestPush() {
  return authedFetch('push-register', 'send_test', {})
}

// The notification-preference flag keys the Notifications card OWNS. Mirrors
// notification-preferences.js FLAGS. The Notifications card must send ONLY
// these on save — never the friend-privacy share_* columns or the
// friend_privacy tier — so the two Profile cards that write the same
// notification_preferences row can't clobber each other's just-made change
// (F-039 two-writer review). See notificationFlagsPayload below.
export const NOTIFICATION_FLAG_KEYS = [
  'push_enabled',
  'round_invites',
  'score_updates',
  'event_reminders',
  'friend_requests',
  'league_standings',
]

// Pick only the notification flag keys present in a (possibly merged) prefs
// object, coerced to strict booleans. Fields the friend-privacy card owns
// (share_*, friend_privacy) are dropped so a Notifications save never rewrites
// them with a stale snapshot. Pure + exported for unit testing.
export function notificationFlagsPayload(prefs) {
  const out = {}
  const src = prefs || {}
  for (const k of NOTIFICATION_FLAG_KEYS) {
    if (k in src) out[k] = Boolean(src[k])
  }
  return out
}

// Preferences.
export async function getNotificationPreferences() {
  return authedFetch('notification-preferences', 'get', {})
}

export async function updateNotificationPreferences(prefs) {
  return authedFetch('notification-preferences', 'update', { prefs })
}
